/* Sales Pipeline · Stap 4
 *
 * 6-column Kanban met 3 lead-bronnen, herkenbaar aan KLEUR:
 *   🟢 Groen   — manueel aangemaakt door mij  (source=manual)
 *   🔵 Blauw   — gevonden door AI Signal Agent (source=agent)
 *   🟣 Paars   — via Pipeline Request brief    (source=pipeline_request)
 *
 * Features:
 *   • Klik op card → detail Drawer (timeline, stakeholders, outreach history)
 *   • "+ Nieuwe lead" → handmatig (groen)
 *   • "+ Pipeline request" → brief → leads direct in pipeline (paars)
 *   • Multi-select met checkbox → bulk-outreach knop (best-channel logic)
 */
import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import { useSearchParams } from "react-router-dom";
import { Plus, Send, Trash2, Archive, Eye, EyeOff } from "lucide-react";
import ScaliqPushPopover from "@/components/sales/ScaliqPushPopover";
import { AddToCampaignPopover } from "@/components/sales/AddToCampaignPopover";

import { PageHeader } from "../Dashboard";
import ForecastPanel from "@/components/sales/ForecastPanel";
import { STAGES, sourceOf, fmtEur } from "@/components/sales/pipeline/constants";
import SourceFilter from "@/components/sales/pipeline/SourceFilter";
import LeadCard from "@/components/sales/pipeline/LeadCard";
import LeadDetailDrawer from "@/components/sales/pipeline/LeadDetailDrawer";
import LeadCreateModal from "@/components/sales/pipeline/LeadCreateModal";
import BulkOutreachModal from "@/components/sales/pipeline/BulkOutreachModal";
import { fireWonConfetti } from "@/components/sales/pipeline/wonConfetti";

export default function SalesPipeline() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    // NOTE: Pipeline Request UI removed in v0.7.4 — Hunt Mission (schedule=manual)
    // covers the same one-shot brief → leads use case with better citation trail.
    const [openLead, setOpenLead] = useState(null);
    const [filterSource, setFilterSource] = useState("all");
    const [selected, setSelected] = useState(new Set());
    const [showBulkOutreach, setShowBulkOutreach] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/leads?limit=200${showArchived ? "&include_archived=true" : ""}`);
            setLeads(r.data);
            // Sprint 6 · #21 — één forecast-fetch per (her)lading, gedeeld door
            // ForecastPanel én de win-kans-chips op de kaarten.
            api.get("/pipeline/forecast").then((fr) => setForecast(fr.data)).catch(() => {});
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [showArchived]);

    const [forecast, setForecast] = useState(null);
    const probMap = useMemo(() => {
        const m = {};
        for (const f of forecast?.leads || []) m[f.id] = f;
        return m;
    }, [forecast]);

    // Drill-down vanaf het dashboard: ?stage=x → scroll naar de lane + highlight
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const stage = searchParams.get("stage");
        if (loading || !stage) return;
        const el = document.querySelector(`[data-testid="sales-stage-${stage}"]`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
            el.classList.add("stage-highlight");
            const t = setTimeout(() => el.classList.remove("stage-highlight"), 3000);
            return () => clearTimeout(t);
        }
    }, [loading]);

    const move = async (id, stage, e) => {
        e?.stopPropagation();
        try {
            await api.put(`/leads/${id}`, { stage });
            if (stage === "won") {
                fireWonConfetti();
                toast.success(i18n.t("pipeline_sales.won_toast"));
            } else {
                toast.success(`→ ${stage}`);
            }
            load();
        }
        catch (err) { toast.error(i18n.t("common.failed")); }
    };

    // Drag & Drop — HTML5 native, no lib. `draggedId` tracks which lead is
    // being moved. When dropped on a stage column, we call `move(id, stage)`.
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverStage, setDragOverStage] = useState(null);
    const onCardDragStart = (leadId, e) => {
        setDraggedId(leadId);
        try { e.dataTransfer.setData("text/plain", leadId); e.dataTransfer.effectAllowed = "move"; } catch (_) {}
    };
    const onCardDragEnd = () => { setDraggedId(null); setDragOverStage(null); };
    const onStageDragOver = (stageId, e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dragOverStage !== stageId) setDragOverStage(stageId);
    };
    const onStageDrop = async (stageId, e) => {
        e.preventDefault();
        const id = draggedId || e.dataTransfer.getData("text/plain");
        setDraggedId(null); setDragOverStage(null);
        if (!id) return;
        const lead = leads.find((l) => l.id === id);
        if (!lead || lead.stage === stageId) return;
        await move(id, stageId);
    };

    const bulkArchive = async () => {
        if (selected.size === 0) return;
        if (!confirm(`${selected.size} companies archiveren?`)) return;
        setBulkBusy(true);
        try {
            const r = await api.post("/leads/bulk-archive", { lead_ids: [...selected] });
            toast.success(r.data.summary);
            setSelected(new Set()); load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pipeline_sales.bulk_archive_failed")); }
        finally { setBulkBusy(false); }
    };
    const bulkDelete = async () => {
        if (selected.size === 0) return;
        if (!confirm(i18n.t("pipeline_sales.bulk_delete_confirm", { count: selected.size }))) return;
        setBulkBusy(true);
        try {
            const r = await api.post("/leads/bulk-delete", { lead_ids: [...selected] });
            toast.success(r.data.summary);
            setSelected(new Set()); load();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("pipeline_sales.bulk_delete_failed")); }
        finally { setBulkBusy(false); }
    };

    const filteredLeads = useMemo(() => {
        if (filterSource === "all") return leads;
        return leads.filter((l) => sourceOf(l) === filterSource);
    }, [leads, filterSource]);

    const counts = useMemo(() => {
        const c = { all: leads.length, manual: 0, agent: 0, pipeline_request: 0 };
        for (const l of leads) c[sourceOf(l)] = (c[sourceOf(l)] || 0) + 1;
        return c;
    }, [leads]);

    const toggleSelect = (id, e) => {
        e?.stopPropagation();
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelected(new Set());

    return (
        <PageHeader
            title="Sales Pipeline"
            subtitle={i18n.t("pipeline_sales.subtitle")}
            actions={
                <div className="flex items-center gap-2">
                    <button onClick={() => setShowCreate(true)} data-testid="pipeline-create-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-success)] text-black text-sm font-medium hover:opacity-90">
                        <Plus size={14} /> Nieuwe lead
                    </button>
                </div>
            }
        >
            {/* Sprint 6 · #21 — Pipeline-voorspelling */}
            <ForecastPanel data={forecast} />

            {/* Source filter row */}
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <SourceFilter counts={counts} value={filterSource} onChange={setFilterSource} />
                <button onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-leads"
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border ${showArchived ? "bg-[var(--jr-brand)]/15 text-[var(--jr-brand)] border-[var(--jr-brand)]/30" : "text-[var(--jr-text-3)] border-white/10 hover:text-white hover:bg-white/5"}`}>
                    {showArchived ? <Eye size={12} /> : <EyeOff size={12} />}
                    {showArchived ? i18n.t("pipeline_sales.archived_shown") : i18n.t("pipeline_sales.archived_hidden")}
                </button>
            </div>

            {/* Bulk action bar (visible when selected.size > 0) */}
            {selected.size > 0 && (
                <div data-testid="bulk-bar"
                    className="sticky top-0 z-20 mb-3 rounded-lg border border-[var(--jr-brand)]/40 bg-[var(--jr-brand)]/[0.10] backdrop-blur px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                    <span className="text-xs text-white">
                        <span className="font-mono text-[var(--jr-brand)]">{selected.size}</span> lead{selected.size === 1 ? "" : "s"} geselecteerd
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={clearSelection} data-testid="bulk-clear"
                            className="text-[10px] font-mono uppercase tracking-[0.16em] px-3 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5">
                            Annuleer
                        </button>
                        <button onClick={bulkArchive} disabled={bulkBusy} data-testid="bulk-archive-leads"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 text-[var(--jr-text-2)] hover:bg-white/5 hover:text-white disabled:opacity-50">
                            <Archive size={12} /> Archiveer
                        </button>
                        <button onClick={bulkDelete} disabled={bulkBusy} data-testid="bulk-delete-leads"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[var(--jr-error)]/40 bg-[var(--jr-error)]/10 text-[var(--jr-error)] hover:bg-[var(--jr-error)]/20 disabled:opacity-50">
                            <Trash2 size={12} /> Verwijder
                        </button>
                        <AddToCampaignPopover entityType="lead" entityIds={[...selected]} onEnrolled={() => { setSelected(new Set()); load(); }} testidPrefix="bulk-atc" buttonLabel={`Voeg ${selected.size} toe`} />
                        <ScaliqPushPopover ids={[...selected]} kind="lead" compact onDone={() => { setSelected(new Set()); load(); }} />
                        <button onClick={() => setShowBulkOutreach(true)} data-testid="bulk-outreach-btn"
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black font-medium hover:bg-[var(--jr-brand-2)]">
                            <Send size={12} /> Genereer outreach voor {selected.size}
                        </button>
                    </div>
                </div>
            )}

            {loading ? <div className="text-[var(--jr-text-2)]">{i18n.t("common.loading")}</div> : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-[repeat(6,minmax(215px,1fr))] gap-3 overflow-x-auto pb-4">
                    {STAGES.map((st) => {
                        // Leads met een onbekende/lege stage (bv. door een oude import)
                        // vallen terug op de NIEUW-lane zodat ze nooit onzichtbaar zijn.
                        const items = filteredLeads.filter((l) =>
                            (STAGES.some((s) => s.id === l.stage) ? l.stage : "new") === st.id);
                        const isDragTarget = dragOverStage === st.id;
                        const laneValue = items.reduce((sum, l) => sum + (Number(l.deal_value) || 0), 0);
                        return (
                            <div key={st.id} data-testid={`sales-stage-${st.id}`}
                                onDragOver={(e) => onStageDragOver(st.id, e)}
                                onDragLeave={() => dragOverStage === st.id && setDragOverStage(null)}
                                onDrop={(e) => onStageDrop(st.id, e)}
                                className={`rounded-xl border bg-[var(--jr-surface)] flex flex-col min-h-[420px] transition-all ${isDragTarget ? "border-[var(--jr-brand)] shadow-[0_0_0_2px_var(--jr-brand)] bg-[var(--jr-brand)]/[0.04]" : "border-white/8"}`}>
                                <div className="px-4 py-3 border-b border-white/8 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} />
                                        <span className="text-xs font-mono uppercase tracking-[0.18em]" style={{ color: st.color }}>{st.label}</span>
                                    </div>
                                    <span className="text-[10px] font-mono text-[var(--jr-text-3)] inline-flex items-center gap-1.5">
                                        {fmtEur(laneValue) && (
                                            <span className="text-[var(--jr-amber)]" data-testid={`lane-value-${st.id}`}
                                                title={i18n.t("pipeline_sales.lane_value_tip")}>{fmtEur(laneValue)}</span>
                                        )}
                                        {items.length}
                                    </span>
                                </div>
                                <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                                    {items.map((l) => (
                                        <LeadCard key={l.id} lead={l}
                                            stageId={st.id}
                                            prob={probMap[l.id]}
                                            onMove={move} onOpen={() => setOpenLead(l)}
                                            selected={selected.has(l.id)}
                                            onToggleSelect={(e) => toggleSelect(l.id, e)}
                                            onDragStart={(e) => onCardDragStart(l.id, e)}
                                            onDragEnd={onCardDragEnd}
                                            isDragging={draggedId === l.id} />
                                    ))}
                                    {isDragTarget && items.length === 0 && (
                                        <div className="text-center text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-brand)] py-8 border-2 border-dashed border-[var(--jr-brand)]/40 rounded-lg">
                                            Drop hier
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showCreate && (
                <LeadCreateModal
                    onClose={() => setShowCreate(false)}
                    onCreated={async () => { setShowCreate(false); await load(); }}
                />
            )}
            {showBulkOutreach && (
                <BulkOutreachModal
                    leadIds={[...selected]}
                    onClose={() => setShowBulkOutreach(false)}
                    onDone={async () => { setShowBulkOutreach(false); clearSelection(); await load(); }}
                />
            )}
            {openLead && (
                <LeadDetailDrawer
                    leadId={openLead.id}
                    initial={openLead}
                    onClose={() => setOpenLead(null)}
                    onChanged={load}
                />
            )}
        </PageHeader>
    );
}
